import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { OrderDetail } from '@/api/orders';
import { STATUS_LABELS, dueTone, fmtDateLong } from '../lib';
import type { NextAction } from '../orderPresentation';
import { StatusText } from '@/components/Status';
import { statusInk } from '@/lib/status';
import { askHref, journeyAsksHref } from '../askLinks';

/**
 * The journey's facts in fixed slots, in a fixed order, every journey. An
 * empty slot shows a dash rather than disappearing, so the strip never
 * reshuffles between journeys and a missing date reads as missing.
 */
export function FactsStrip({ detail, next }: { detail: OrderDetail; next: NextAction }) {
  const location = useLocation();
  const refs = (type: string) => detail.refs.filter((r) => r.refType === type).map((r) => r.value);
  const orderNos = refs('order_no');
  const poNos = detail.poId != null ? [String(detail.poId), ...refs('po_no')] : refs('po_no');
  const invoiceNos = detail.invoices.map((i) => i.invoiceNo).filter((v): v is string => Boolean(v));
  const openDecisions = detail.actions.length;
  const dash = <span className="text-muted-foreground">—</span>;
  const date = (value: string | null, className?: string) =>
    value ? <span className={cn('tabular-nums', className)}>{fmtDateLong(value)}</span> : dash;
  const list = (values: string[]) => (values.length ? <span className="tabular-nums">{values.join(', ')}</span> : dash);

  // Only an urgent window colours the date; a far-off one reads like the other dates.
  const orderByTone = detail.status === 'announced' ? dueTone(detail.orderDueDate) : null;

  const slots: Array<[string, React.ReactNode]> = [
    ['Status', STATUS_LABELS[detail.status] || detail.status],
    ['Next', <StatusText tone={next.tone}>{next.label}</StatusText>],
    ['Order by', date(detail.orderDueDate, orderByTone && orderByTone !== 'neutral' ? statusInk(orderByTone) : undefined)],
    ['Placed', date(detail.placedDate)],
    ['Ships', date(detail.expectedAvailability)],
    ['Embargo', date(detail.embargoDate)],
    ['Supplier', detail.supplierId != null ? <span className="tabular-nums">#{detail.supplierId}</span> : dash],
    ['PO', list(poNos)],
    ['Order no', list(orderNos)],
    ['Invoice no', list(invoiceNos)],
    ['Receivings', list(detail.receivingIds.map((id) => `#${id}`))],
    ['Open decisions', openDecisions > 0 ? (
      <Link to={openDecisions === 1 ? askHref(detail.actions[0].id) : journeyAsksHref(detail.id)} state={{ returnTo: location.pathname + location.search }} className={cn('tabular-nums hover:underline', statusInk('warning'))}>
        {openDecisions}
      </Link>
    ) : dash],
  ];

  return (
    <dl className="mx-8 mb-6 grid grid-cols-6 gap-px overflow-hidden rounded-[10px] border border-border bg-border max-[1200px]:grid-cols-4 max-md:mx-4 max-md:mb-5 max-md:grid-cols-2">
      {slots.map(([label, value]) => (
        <div key={label} className="min-w-0 bg-card px-4 py-3"><dt className="mb-1 text-[11px] tracking-[.02em] text-muted-foreground">{label}</dt><dd className="truncate text-[13.5px] font-medium">{value}</dd></div>
      ))}
    </dl>
  );
}
